import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useAuth } from "../context/authContext";
import { useNavigate } from "react-router-dom";
import { confirmAndLogout } from "../utils/session";
import { ordenarFranjas } from "../utils/franjas";

// Colores para distinguir de un vistazo a qué supermercado pertenece cada pedido
const SUPERMERCADO_COLORES = {
  M: { bg: "#dbeafe", color: "#1d4ed8", border: "#93c5fd" },
  B: { bg: "#ffedd5", color: "#c2410c", border: "#fdba74" },
};
const getSupermercadoStyle = (s) =>
  SUPERMERCADO_COLORES[(s || "").toUpperCase()] || { bg: "#f3f4f6", color: "#374151", border: "#d1d5db" };
const SupermercadoBadge = ({ valor }) => {
  if (!valor) return null;
  const sc = getSupermercadoStyle(valor);
  return (
    <span style={{
      display: "inline-block", fontSize: 11, fontWeight: 800, padding: "1px 8px",
      borderRadius: 999, marginLeft: 6, letterSpacing: "0.3px",
      background: sc.bg, color: sc.color, border: `1.5px solid ${sc.border}`,
    }}>
      {valor.toUpperCase()}
    </span>
  );
};

export default function Repartidor() {
  const { user } = useAuth();
  const usuario = user?.user || user;

  // Paneles: 'seleccionar' | 'asignados' | 'enReparto' (esta última sin pestaña visible)
  const [activePanel, setActivePanel] = useState("seleccionar");

  // Datos por panel
  const [comprados, setComprados] = useState([]); // estado='comprado'
  const [asignadosPendientes, setAsignadosPendientes] = useState([]); // en reparto sin dia_recogida
  const [enReparto, setEnReparto] = useState([]); // en reparto con dia_recogida

  // Filtros
  const [filtroAbreviatura, setFiltroAbreviatura] = useState("");
  const [filtroFranja, setFiltroFranja] = useState("Todas");
  const [filtroSupermercado, setFiltroSupermercado] = useState("Todos");

  const [cargando, setCargando] = useState(false);
  const [franjasDisponibles, setFranjasDisponibles] = useState([]);
  const [supermercadosDisponibles, setSupermercadosDisponibles] = useState([]);
  const [bolsasPedidos, setBolsasPedidos] = useState(() => {
    const bolsasGuardadas = localStorage.getItem("bolsasPedidos");
    return bolsasGuardadas ? JSON.parse(bolsasGuardadas) : {};
  });
  const [modalEntrega, setModalEntrega] = useState({ abierto: false, pedido: null });
  const [estadoSeleccionado, setEstadoSeleccionado] = useState("entregado");
  const [fotoEntrega, setFotoEntrega] = useState(null);
  const [procesandoFoto, setProcesandoFoto] = useState(false);
  const [subiendo, setSubiendo] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [previewUrl, setPreviewUrl] = useState(null);
  const previewRef = useRef(null);
  // Marca temporal para refrescar colores de tarjetas automáticamente
  const [nowTick, setNowTick] = useState(Date.now());
  // Repartidores disponibles para intercambio
  const [repartidores, setRepartidores] = useState([]);
  const [swapUI, setSwapUI] = useState({ abierto: false, pedidoId: null, seleccionado: "" });

  const navigate = useNavigate();
  const [notifsEnabled, setNotifsEnabled] = useState(false);

  useEffect(() => {
    try {
      const stored = sessionStorage.getItem('enRepartidor_notifs');
      setNotifsEnabled(stored === '1');
    } catch (_) {}
  }, []);

  const toggleNotifs = () => {
    try { const next = !notifsEnabled; sessionStorage.setItem('enRepartidor_notifs', next ? '1' : '0'); setNotifsEnabled(next); }
    catch (_) { setNotifsEnabled(s => !s); }
  };

  const handleToggleNotifs = () => {
    const next = !notifsEnabled;
    const pregunta = next ? '🔔 ¿Habilitar notificaciones? 🔔' : '🔕 ¿Deshabilitar notificaciones? 🔕';
    const confirmar = window.confirm(pregunta);
    if (!confirmar) return;
    try { sessionStorage.setItem('enRepartidor_notifs', next ? '1' : '0'); setNotifsEnabled(next); } catch (_) { setNotifsEnabled(s=>!s); }
  };

  const handleLogout = () => {
    confirmAndLogout(navigate, '/login');
  };

  // Helper: formatear bytes a texto legible
  const formatBytes = (bytes) => {
    if (!bytes && bytes !== 0) return "";
    const sizes = ["B", "KB", "MB", "GB"]; 
    if (bytes === 0) return "0 B";
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    const val = bytes / Math.pow(1024, i);
    return `${val.toFixed(val >= 100 || i === 0 ? 0 : 1)} ${sizes[i]}`;
  };

  // device id helper (same logic as Login.getDeviceIdLocal)
  const getDeviceIdLocal = () => {
    try {
      let id = localStorage.getItem('device_id');
      if (!id) {
        id = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : Math.random().toString(36).slice(2, 10);
        localStorage.setItem('device_id', id);
      }
      return id;
    } catch (e) {
      return 'unknown-device';
    }
  };

  // Helper: comprimir imagen en el cliente usando canvas
  // - maxDim controla el tamaño máximo del lado mayor (p. ej. 1600px)
  // - quality entre 0 y 1
  const compressImage = async (file, maxDim = 1600, quality = 0.7) => {
    if (!(file instanceof Blob)) return file;
    // Si ya es pequeña (< 250KB), no comprimir para ahorrar tiempo
    if (file.size && file.size < 250 * 1024) return file;
    return new Promise((resolve, reject) => {
      try {
        const img = new Image();
        const url = URL.createObjectURL(file);
        img.onload = () => {
          try {
            let { width, height } = img;
            const scale = Math.min(1, maxDim / Math.max(width, height));
            const targetW = Math.round(width * scale);
            const targetH = Math.round(height * scale);
            const canvas = document.createElement('canvas');
            canvas.width = targetW;
            canvas.height = targetH;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, targetW, targetH);
            const newName = (file.name || 'foto').replace(/\.[^.]+$/, '') + '.jpg';
            if (typeof canvas.toBlob === 'function') {
              canvas.toBlob(
                (blob) => {
                  URL.revokeObjectURL(url);
                  if (!blob) {
                    // toBlob devolvió null: fallback via toDataURL
                    try {
                      const dataUrl = canvas.toDataURL('image/jpeg', quality);
                      const byteStr = atob(dataUrl.split(',')[1]);
                      const arr = new Uint8Array(byteStr.length);
                      for (let i = 0; i < byteStr.length; i++) arr[i] = byteStr.charCodeAt(i);
                      const fallbackBlob = new Blob([arr], { type: 'image/jpeg' });
                      try { resolve(new File([fallbackBlob], newName, { type: 'image/jpeg' })); }
                      catch (_) { fallbackBlob.name = newName; resolve(fallbackBlob); }
                    } catch (_) { resolve(file); }
                    return;
                  }
                  try {
                    const compressedFile = new File([blob], newName, { type: 'image/jpeg' });
                    resolve(compressedFile);
                  } catch (_) {
                    blob.name = newName;
                    resolve(blob);
                  }
                },
                'image/jpeg',
                quality
              );
            } else {
              // Navegadores sin toBlob: usar toDataURL
              URL.revokeObjectURL(url);
              try {
                const dataUrl = canvas.toDataURL('image/jpeg', quality);
                const byteStr = atob(dataUrl.split(',')[1]);
                const arr = new Uint8Array(byteStr.length);
                for (let i = 0; i < byteStr.length; i++) arr[i] = byteStr.charCodeAt(i);
                const fallbackBlob = new Blob([arr], { type: 'image/jpeg' });
                try { resolve(new File([fallbackBlob], newName, { type: 'image/jpeg' })); }
                catch (_) { fallbackBlob.name = newName; resolve(fallbackBlob); }
              } catch (_) { resolve(file); }
            }
          } catch (err) {
            URL.revokeObjectURL(url);
            resolve(file);
          }
        };
        img.onerror = () => {
          URL.revokeObjectURL(url);
          resolve(file);
        };
        img.src = url;
      } catch (err) {
        resolve(file);
      }
    });
  };

  // Bootstrap: cargar listas y decidir panel inicial
  useEffect(() => {
    if (!usuario) return;
    const bootstrap = async () => {
      // Ensure session_token exists for this device/user so other pages (notifications etc.) can read it
      try {
        const existing = localStorage.getItem('session_token');
        if (!existing) {
          const deviceId = getDeviceIdLocal();
          const userStr = usuario + '';
          localStorage.setItem('session_token', JSON.stringify({ user: userStr, device_id: deviceId }));
        }
      } catch (_) {}
      setCargando(true);
      await Promise.all([fetchComprados(), fetchAsignadosPendientes(), fetchEnReparto()]);
      try {
        const res = await axios.get(`/api/compras/reparto`, {
          params: { estado: "en reparto", user_repartidor: usuario, con_dia_recogida: "true" },
        });
        const tieneEnRepartoConDia = Array.isArray(res.data) && res.data.length > 0;
        setActivePanel(tieneEnRepartoConDia ? "enReparto" : "seleccionar");
      } catch {
        setActivePanel("seleccionar");
      } finally {
        setCargando(false);
      }
    };
    bootstrap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usuario]);

  // Control del botón Atrás del navegador
  useEffect(() => {
    const handlePopState = (e) => {
      if (activePanel === "enReparto") {
        // Bloquear volver atrás bajo cualquier circunstancia mientras haya reparto activo
        window.history.pushState(null, "", window.location.href);
        return;
      }
      const didLogout = confirmAndLogout(navigate, '/login');
      if (!didLogout) {
        try { window.history.pushState(null, "", window.location.href); } catch(_) {}
      }
    };
    window.history.pushState(null, "", window.location.href);
    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [navigate, activePanel]);

  // Al entrar en Panel 3, reforzar el lock del historial
  useEffect(() => {
    if (activePanel === "enReparto") {
      try {
        window.history.pushState(null, "", window.location.href);
      } catch (_) {}
    }
  }, [activePanel]);

  // topbar handlers: render buttons same height and non overlapping

  // Cargar franjas desde pisos y los supermercados disponibles se derivan de compras (tabla compras)
  useEffect(() => {
    const cargarFranjas = async () => {
      try {
        const rf = await axios.get(`/api/pisos/franjas`);
        const franjas = Array.isArray(rf.data) ? rf.data.filter(Boolean) : [];
        setFranjasDisponibles(ordenarFranjas(franjas));
      } catch (e) {
        console.warn("No se pudieron cargar franjas", e);
        setFranjasDisponibles([]);
      }
    };
    cargarFranjas();
  }, []);

  // Derivar opciones de supermercado desde lo que viene en 'compras' (tabla compras)
  useEffect(() => {
    const all = [...comprados, ...asignadosPendientes, ...enReparto];
    const vals = Array.from(
      new Set(
        all
          .map((c) => (c.supermercado || "").toString().trim())
          .filter((s) => s && s !== "-")
      )
    );
    setSupermercadosDisponibles(vals.sort((a,b)=> a.localeCompare(b, 'es', {sensitivity:'base'})));
  }, [comprados, asignadosPendientes, enReparto]);

  // Si el filtro actual deja de estar disponible, volver a "Todos"
  useEffect(() => {
    if (filtroSupermercado !== 'Todos' && !supermercadosDisponibles.includes(filtroSupermercado)) {
      setFiltroSupermercado('Todos');
    }
  }, [supermercadosDisponibles]);

  // Auto-refresh: actualizar cada 30s para recomputar colores por franja
  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 30 * 1000);
    return () => clearInterval(id);
  }, []);

  // Cargar lista de repartidores (una vez)
  const cargarRepartidores = async () => {
    if (repartidores.length > 0) return;
    try {
      const res = await axios.get(`/api/usuarios/repartidores`);
      const data = res.data?.data || [];
      setRepartidores(Array.isArray(data) ? data : []);
    } catch (e) {
      console.warn("No se pudieron cargar repartidores", e);
      setRepartidores([]);
    }
  };

  // Persistir bolsas
  useEffect(() => {
    localStorage.setItem("bolsasPedidos", JSON.stringify(bolsasPedidos));
  }, [bolsasPedidos]);

  // Limpiar previewUrl anterior al desmontar o cuando cambie
  useEffect(() => {
    return () => {
      try {
        if (previewUrl) URL.revokeObjectURL(previewUrl);
      } catch (_) {}
    };
  }, [previewUrl]);

  // Recuperar bolsas guardadas (por si refrescan)
  useEffect(() => {
    const bolsasGuardadas = localStorage.getItem("bolsasPedidos");
    if (bolsasGuardadas) {
      setBolsasPedidos(JSON.parse(bolsasGuardadas));
    }
  }, []);

  // Cargas de datos
  const fetchComprados = async () => {
    try {
      const res = await axios.get(`/api/compras/reparto`, {
        params: { estado: "comprado" },
      });
      setComprados(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error("Error cargando compras 'comprado':", e);
      setComprados([]);
    }
  };

  const fetchAsignadosPendientes = async () => {
    if (!usuario) return;
    try {
      const res = await axios.get(`/api/compras/reparto`, {
        params: { estado: "en reparto", user_repartidor: usuario, con_dia_recogida: "false" },
      });
      setAsignadosPendientes(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error("Error cargando asignados pendientes:", e);
      setAsignadosPendientes([]);
    }
  };

  const fetchEnReparto = async () => {
    if (!usuario) return;
    try {
      const res = await axios.get(`/api/compras/reparto`, {
        params: { estado: "en reparto", user_repartidor: usuario, con_dia_recogida: "true" },
      });
      setEnReparto(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error("Error cargando en reparto:", e);
      setEnReparto([]);
    }
  };

  const handleBolsasChange = (pedidoId, value) => {
    if (!/^\d*$/.test(value)) return;
    setBolsasPedidos((prev) => ({ ...prev, [pedidoId]: value }));
  };

  const abrirModalEntrega = (pedido) => {
    setEstadoSeleccionado("entregado");
    try {
      if (previewRef.current) {
        try { URL.revokeObjectURL(previewRef.current); } catch (_) {}
        previewRef.current = null;
      }
    } catch (_) {}
    setPreviewUrl(null);
    setFotoEntrega(null);
    setModalEntrega({ abierto: true, pedido });
  };

  const cerrarModalEntrega = () => {
    try {
      if (previewRef.current) {
        try { URL.revokeObjectURL(previewRef.current); } catch (_) {}
        previewRef.current = null;
      }
    } catch (_) {}
    setPreviewUrl(null);
    setFotoEntrega(null);
    setModalEntrega({ abierto: false, pedido: null });
    setProcesandoFoto(false);
    setSubiendo(false);
    setUploadProgress(0);
  };

  
  const sendNotificationToSupervisor = async (pedido, estado) => {
    try {
      // Tipo de notificación según estado
      const tipo = (estado === 'entregado') ? 'entregado' : (estado === 'en la puerta') ? 'en_la_puerta' : 'actualizacion_estado';

      // Llamada al backend: crea un endpoint que reciba esto y haga el push a la supervisora
      await axios.post('/device-notifs/notify_compra', { compra_id: pedido.id }, { withCredentials: true });
      // console.info('Notificación enviada a supervisora', { compra_id: pedido.id, user_supervisora, tipo });
    } catch (err) {
      console.warn('Error en sendNotificationToSupervisor', err);
      throw err;
    }
  };

  const confirmarEntrega = async () => {
    const { pedido } = modalEntrega;
    try {
      // Si es "en la puerta" y hay foto -> usar endpoint con foto
      if (estadoSeleccionado === 'en la puerta') {
        if (!fotoEntrega) {
          alert('Necesitas añadir una foto de entrega');
          return;
        }

        setSubiendo(true);
        setUploadProgress(0);
        const fd = new FormData();
        fd.append('id', pedido.id);
        fd.append('user_repartidor', usuario);
        fd.append('estado', 'en la puerta');
        fd.append('foto', fotoEntrega);

        await axios.post(
          `/api/compras/entregar_con_foto`,
          fd,
          {
            onUploadProgress: (evt) => {
              if (evt && evt.total) {
                const pct = Math.round((evt.loaded / evt.total) * 100);
                setUploadProgress(pct);
              }
            },
          }
        );
        setSubiendo(false);
      } else {
        await axios.post(
          `/api/compras/entregar?id=${pedido.id}&user_repartidor=${encodeURIComponent(
            usuario
          )}&estado=${estadoSeleccionado}`
        );
      }
      
      // Envío de notificación a la supervisora correspondiente
      try {
        await sendNotificationToSupervisor(pedido, estadoSeleccionado);
      } catch (notifyErr) {
        console.warn('Error enviando notificación a supervisora', notifyErr);
      }

      await fetchEnReparto();
      // Si ya no quedan pedidos en reparto (con día), volver automáticamente al Panel 1
      try {
        const res = await axios.get(`/api/compras/reparto`, {
          params: { estado: "en reparto", user_repartidor: usuario, con_dia_recogida: "true" },
        });
        const quedan = Array.isArray(res.data) ? res.data.length : 0;
        if (quedan === 0) {
          setActivePanel("seleccionar");
        }
      } catch (_) {}
      alert(
        `✅ Pedido ${pedido.abreviatura_piso} (${pedido.n_bolsas || 0} bolsas) marcado como "${estadoSeleccionado}"`
      );
    } catch (e) {
      console.error(e);
      alert("❌ Error al actualizar el estado del pedido");
    } finally {
      // Usar helper para limpiar preview y estados relacionados
      try { cerrarModalEntrega(); } catch (_) { setModalEntrega({ abierto: false, pedido: null }); setFotoEntrega(null); setUploadProgress(0); setSubiendo(false); }
    }
  };

  const asignarCompra = async (pedido) => {
    const bolsas = bolsasPedidos[pedido.id];
    const bolsasNum = parseInt(bolsas, 10);
    if (!bolsas || isNaN(bolsasNum) || bolsasNum <= 0) {
      alert("Introduce 'Nº 🛍️' válido (mayor que 0)");
      return;
    }
    const confirmar = window.confirm(
      `¿Asignar ${pedido.abreviatura_piso} con Nº ${bolsasNum} bolsas?`
    );
    if (!confirmar) return;
    try {
      const resp = await axios.post(
        `/api/compras/asignar`,
        { id: pedido.id, user_repartidor: usuario, n_bolsas: bolsasNum },
        { headers: { "Content-Type": "application/json" } }
      );
      if (resp.data?.ok) {
        await Promise.all([fetchComprados(), fetchAsignadosPendientes()]);
      }
    } catch (e) {
      const msg = e.response?.data?.error || "Error al asignar";
      alert(msg);
      await fetchComprados();
    }
  };

  const soltarCompra = async (pedido) => {
    const confirmar = window.confirm(`¿Quitar ${pedido.abreviatura_piso} del reparto?`);
    if (!confirmar) return;
    try {
      const resp = await axios.post(
        `/api/compras/soltar`,
        { id: pedido.id, user_repartidor: usuario },
        { headers: { "Content-Type": "application/json" } }
      );
      if (resp.data?.ok) {
        await Promise.all([fetchAsignadosPendientes(), fetchComprados()]);
      }
    } catch (e) {
      const msg = e.response?.data?.error || "Error al soltar";
      alert(msg);
      await fetchAsignadosPendientes();
    }
  };

  const empezarReparto = async () => {
    if (asignadosPendientes.length === 0) {
      alert("No hay pedidos asignados para iniciar el reparto.");
      return;
    }
    const total = asignadosPendientes.length;
    if (!window.confirm(`¿Iniciar reparto para ${total} pedido(s)?`)) return;
    try {
      await axios.post(
        `/api/compras/iniciar_reparto`,
        { user_repartidor: usuario },
        { headers: { "Content-Type": "application/json" } }
      );
      await Promise.all([fetchAsignadosPendientes(), fetchEnReparto()]);
      setActivePanel("enReparto"); // Entrar al panel 3; no se puede volver atrás por UI
    } catch (error) {
      console.error("Error al iniciar reparto:", error);
      alert("Ocurrió un error al iniciar el reparto.");
    }
  };

  // Intercambio de pedido con otro repartidor (Panel 3)
  const abrirIntercambio = async (pedido) => {
    await cargarRepartidores();
    setSwapUI({ abierto: true, pedidoId: pedido.id, seleccionado: "" });
  };

  const cancelarIntercambio = () => setSwapUI({ abierto: false, pedidoId: null, seleccionado: "" });

  const confirmarIntercambio = async (pedido) => {
    const otro = swapUI.seleccionado;
    if (!otro) {
      alert("Selecciona un repartidor");
      return;
    }
    const bolsasTxt = pedido.n_bolsas ? ` (${pedido.n_bolsas} bolsas)` : "";
    if (!window.confirm(`¿Entregar el pedido ${pedido.abreviatura_piso}${bolsasTxt} a ${otro}?`)) return;
    try {
      await axios.post(
        `/api/compras/intercambiar`,
        {
          id: pedido.id,
          user_repartidor_entrega: usuario,
          user_repartidor_recoge: otro,
        },
        { headers: { "Content-Type": "application/json" } }
      );
      await fetchEnReparto();
      setSwapUI({ abierto: false, pedidoId: null, seleccionado: "" });
      alert(`✅ Pedido ${pedido.abreviatura_piso} entregado a ${otro}`);
    } catch (e) {
      const msg = e.response?.data?.error || "Error al intercambiar";
      alert(msg);
    }
  };

  const hayPedidosEnReparto = useMemo(
    () => enReparto.some((p) => p.estado === "en reparto" && p.user_repartidor === usuario),
    [enReparto, usuario]
  );

  // Filtro común
  const filtroComun = (p) => {
    const matchAbreviatura = (p.abreviatura_piso || "")
      .toString()
      .toLowerCase()
      .includes((filtroAbreviatura || "").toString().toLowerCase());
    const matchFranja = filtroFranja === "Todas" || (p.franja_horaria || "") === filtroFranja;
    const matchSupermercado =
      filtroSupermercado === "Todos" || (p.supermercado || "") === filtroSupermercado;
    return matchAbreviatura && matchFranja && matchSupermercado;
  };
  const compradosFiltrados = comprados.filter(filtroComun);
  const asignadosPendientesFiltrados = asignadosPendientes.filter(filtroComun);
  const enRepartoFiltrados = enReparto.filter(filtroComun);

  // Estilos helpers
  const cardBase = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "14px 16px",
    marginBottom: 10,
    borderRadius: 10,
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    border: "1px solid #eee",
  };

  // Colores por franja en tonos claros
  const timeColors = {
    azul: { bg: "#e7f0ff", border: "6px solid #b3d1ff", text: "#233" },
    azulOscuro: { bg: "#839fcaff", border: "6px solid #80b7ff", text: "#233" },
    rojo: { bg: "#dd7a7aff", border: "6px solid #ff9999", text: "#332" },
    naranja: { bg: "#dbb87fff", border: "6px solid #ffcc80", text: "#332" },
    verde: { bg: "#a8e7adff", border: "6px solid #a5d6a7", text: "#233" },
  };

  // Determinar color en base a franja y tiempo actual
  const getColorKeyForPedido = (p) => {
    const fr = (p.franja_horaria || "").trim(); 
    if (fr === "-") return "azul";
    // Esperado formato: hh:mm-hh:mm
    const parts = fr.split("-");
    if (parts.length !== 2) return "verde";
    const end = parts[1]?.trim();
    if (!/^\d{2}:\d{2}$/.test(end)) return "verde";
    // Crear fecha de hoy con la hora de fin
    const [h, m] = end.split(":").map((x) => parseInt(x, 10));
    const now = new Date(nowTick);
    // Para pruebas: fija "ahora" a las 11:30 de hoy (descomenta la siguiente línea)
    // now.setHours(11, 30, 0, 0);
    const endDate = new Date(now);
    endDate.setHours(h, m, 0, 0);
    const nowPlus20 = new Date(now.getTime() + 20 * 60 * 1000);
    if (now >= endDate) return "rojo";
    if (nowPlus20 >= endDate) return "naranja";
    return "verde";
  };

  // Estilo para tarjetas en Panel 3 (enReparto) con colores dinámicos
  const getCardStyleEnReparto = (p) => {
    const key = getColorKeyForPedido(p);
    const color = timeColors[key] || timeColors.verde;
    return {
      ...cardBase,
      backgroundColor: color.bg,
      color: color.text,
      borderLeft: color.border,
    };
  };

  // Estilo para tarjetas en Panel 1 (seleccionar): siempre azul clarito
  // y si el Nº de bolsas es válido (>0) se muestra un azul un poco más oscuro
  const getCardStyleSeleccionar = (p) => {
    const val = bolsasPedidos[p.id];
    const n = parseInt(val, 10);
    const tieneBolsasValidas = !!val && !isNaN(n) && n > 0;
    const color = tieneBolsasValidas ? timeColors.azulOscuro : timeColors.azul;
    return {
      ...cardBase,
      backgroundColor: color.bg,
      color: color.text,
      borderLeft: color.border,
    };
  };

  // Estilo para tarjetas en Panel 2 (asignados): neutro, sin colores por tiempo
  const getCardStyleAsignados = () => ({
    ...cardBase,
    backgroundColor: "#fff",
    color: "#233",
    borderLeft: "6px solid #eee",
  });

  const renderTabs = () => {
    if (activePanel === "enReparto") return null; // ocultar pestañas cuando está en panel 3
    return (
      <div style={{ display: "flex", gap: 8, justifyContent: "center", marginBottom: 16 }}>
        <button
          onClick={() => setActivePanel("seleccionar")}
          style={{
            padding: "8px 14px",
            borderRadius: 6,
            border: activePanel === "seleccionar" ? "2px solid #007bff" : "1px solid #ccc",
            background: activePanel === "seleccionar" ? "#eaf3ff" : "#fff",
            cursor: "pointer",
            fontWeight: 600,
          }}
        >
          Seleccionar pedidos
        </button>
        <button
          onClick={() => setActivePanel("asignados")}
          style={{
            padding: "8px 14px",
            borderRadius: 6,
            border: activePanel === "asignados" ? "2px solid #007bff" : "1px solid #ccc",
            background: activePanel === "asignados" ? "#eaf3ff" : "#fff",
            cursor: "pointer",
            fontWeight: 600,
          }}
        >
          Pedidos asignados
        </button>
      </div>
    );
  };

  return (
    <div style={{ maxWidth: 760, margin: "auto", fontFamily: "Arial, sans-serif", padding: 20, position: 'relative' }}>
      <div style={{ position: 'absolute', top: 12, left: 12, right: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', zIndex: 50 }}>
        {/* <button
          onClick={handleToggleNotifs}
          style={{
            padding: '10px 14px',
            borderRadius: 10,
            backgroundColor: notifsEnabled ? '#10b981' : '#e5e7eb',
            color: notifsEnabled ? '#fff' : '#111827',
            border: 'none',
            cursor: 'pointer',
            fontWeight: 700,
          }}
          title={notifsEnabled ? 'Notificaciones habilitadas' : 'Notificaciones deshabilitadas'}
        >
          {notifsEnabled ? '🔔 Notificaciones habilitadas' : '🔕 Notificaciones deshabilitadas'}
        </button> */}

        <button
          onClick={handleLogout}
          style={{
            padding: '12px 16px',
            borderRadius: 10,
            backgroundColor: '#ef4444',
            color: '#fff',
            border: 'none',
            cursor: 'pointer',
            fontWeight: 800,
            fontSize: 15,
          }}
          title="Cerrar sesión"
        >
          Cerrar sesión
        </button>
      </div>
      <h2 style={{ textAlign: "center", marginBottom: 40, marginTop: 60, color: "#333" }}>
        🚚 Panel Repartidor <strong style={{ color: "#007bff" }}>{usuario}</strong>
      </h2>

      {renderTabs()}

      {/* Filtros comunes */}
      <div
        style={{
          display: "flex",
          gap: 12,
          marginBottom: 16,
          flexWrap: "wrap",
          justifyContent: "center",
        }}
      >
        <input
          type="text"
          value={filtroAbreviatura}
          onChange={(e) => setFiltroAbreviatura(e.target.value)}
          placeholder="Filtrar por piso (Ej: ESQ31.5C)"
          style={{
            padding: "8px 10px",
            borderRadius: 6,
            border: "1px solid #ccc",
            flex: "1 1 auto",
            minWidth: 180,
          }}
        />

        <select
          value={filtroFranja}
          onChange={(e) => setFiltroFranja(e.target.value)}
          style={{
            padding: "6px 10px", // más pequeño
            borderRadius: 6,
            border: "1px solid #ccc",
            flex: "1 1 auto",
            minWidth: 150,
          }}
        >
          <option value="Todas">Todas las franjas</option>
          {franjasDisponibles.map((f) => (
            <option key={f} value={f}>
              {f}
            </option>
          ))}
        </select>

        <select
          value={filtroSupermercado}
          onChange={(e) => setFiltroSupermercado(e.target.value)}
          style={{
            padding: "6px 10px",
            borderRadius: 6,
            border: "1px solid #ccc",
            flex: "1 1 auto",
            minWidth: 160,
          }}
        >
          <option value="Todos">Todos los supermercados</option>
          {supermercadosDisponibles.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      {/* Panel 1: Seleccionar pedidos (comprado) */}
      {activePanel === "seleccionar" && (
        <>
          {cargando ? (
            <p style={{ textAlign: "center" }}>⏳ Cargando...</p>
          ) : compradosFiltrados.length === 0 ? (
            <p style={{ textAlign: "center", color: "#999" }}>
              No hay pedidos "comprado" que coincidan.
            </p>
          ) : (
            <ul style={{ listStyle: "none", padding: 0 }}>
              {compradosFiltrados.map((pedido) => {
                const bolsasVal = bolsasPedidos[pedido.id] || "";
                return (
                  <li key={pedido.id} style={getCardStyleSeleccionar(pedido)}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <div>
                        <div style={{ fontWeight: 700 }}>{pedido.abreviatura_piso}<SupermercadoBadge valor={pedido.supermercado} /></div>
                        <div style={{ fontSize: 12, color: "#666" }}>{pedido.franja_horaria}</div>
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <input
                        type="number"
                        min="1"
                        placeholder="Nº 🛍️"
                        value={bolsasVal}
                        onChange={(e) => handleBolsasChange(pedido.id, e.target.value)}
                        style={{
                          width: 90,
                          padding: "12px 10px",
                          borderRadius: 8,
                          border: "1px solid #ccc",
                          outline: "none",
                          background: "#fff",
                          color: "#333",
                          textAlign: "center",
                        }}
                      />
                      <button
                        title="Asignar al reparto"
                        onClick={() => asignarCompra(pedido)}
                        style={{
                          backgroundColor: "#007bff",
                          color: "white",
                          border: "none",
                          padding: "10px 12px",
                          borderRadius: 8,
                          cursor: "pointer",
                          fontWeight: "bold",
                          boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
                        }}
                      >
                        ➡️
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}

      {/* Panel 2: Pedidos asignados (en reparto sin dia_recogida) */}
      {activePanel === "asignados" && (
        <>
          {asignadosPendientesFiltrados.length === 0 ? (
            <p style={{ textAlign: "center", color: "#999" }}>No tienes pedidos asignados.</p>
          ) : (
            <ul style={{ listStyle: "none", padding: 0 }}>
              {asignadosPendientesFiltrados.map((pedido) => (
                <li key={pedido.id} style={getCardStyleAsignados(pedido)}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div>
                      <div style={{ fontWeight: 700 }}>{pedido.abreviatura_piso}<SupermercadoBadge valor={pedido.supermercado} /></div>
                      <div style={{ fontSize: 12, color: "#666" }}>{pedido.franja_horaria}</div>
                    </div>
                    {pedido.n_bolsas ? (
                      <span style={{ fontWeight: 600 }}>Nº 🛍️ {pedido.n_bolsas}</span>
                    ) : null}
                  </div>
                  <button
                    title="Quitar del reparto"
                    onClick={() => soltarCompra(pedido)}
                    style={{
                      backgroundColor: "#dc3545",
                      color: "white",
                      border: "none",
                      padding: "8px 10px",
                      borderRadius: 8,
                      cursor: "pointer",
                      fontWeight: "bold",
                      boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
                    }}
                  >
                    ✖️
                  </button>
                </li>
              ))}
            </ul>
          )}

          <button
            onClick={empezarReparto}
            style={{
              marginTop: 20,
              width: "100%",
              padding: "14px",
              backgroundColor: "#28a745",
              border: "none",
              color: "white",
              borderRadius: 10,
              cursor: "pointer",
              fontWeight: "bold",
              fontSize: 18,
              boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
            }}
          >
            🚀 Iniciar reparto
          </button>
        </>
      )}

      {/* Panel 3: En reparto (sin pestañas; sólo tras iniciar) */}
      {activePanel === "enReparto" && (
        <>
          {enRepartoFiltrados.length === 0 ? (
            <p style={{ textAlign: "center", color: "#999" }}>No tienes pedidos en reparto.</p>
          ) : (
            <ul style={{ listStyle: "none", padding: 0 }}>
              {enRepartoFiltrados.map((pedido) => (
                <li key={pedido.id} style={getCardStyleEnReparto(pedido)}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div>
                      <div style={{ fontWeight: 700 }}>{pedido.abreviatura_piso}<SupermercadoBadge valor={pedido.supermercado} /></div>
                      <div style={{ fontSize: 12 }}>{pedido.franja_horaria}</div>
                    </div>
                    {pedido.n_bolsas ? (
                      <span style={{ fontWeight: 700 }}>{pedido.n_bolsas} 🛍️</span>
                    ) : null}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {swapUI.abierto && swapUI.pedidoId === pedido.id ? (
                      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        <select
                          value={swapUI.seleccionado}
                          onChange={(e) => setSwapUI((s) => ({ ...s, seleccionado: e.target.value }))}
                          style={{ padding: "6px 8px", borderRadius: 8, border: "1px solid #ccc" }}
                        >
                          <option value="">Selecciona repartidor</option>
                          {repartidores
                            .filter((r) => r.user !== usuario)
                            .map((r) => (
                              <option key={r.user} value={r.user}>
                                {r.user}
                              </option>
                            ))}
                        </select>
                        <div style={{ display: "flex", gap: 8 }}>
                          <button
                            onClick={() => confirmarIntercambio(pedido)}
                            style={{
                              backgroundColor: "#17a2b8",
                              color: "white",
                              border: "none",
                              padding: "6px 10px",
                              borderRadius: 8,
                              cursor: "pointer",
                              fontWeight: "bold",
                            }}
                          >
                            Aceptar
                          </button>
                          <button
                            onClick={cancelarIntercambio}
                            style={{
                              backgroundColor: "#6c757d",
                              color: "white",
                              border: "none",
                              padding: "6px 10px",
                              borderRadius: 8,
                              cursor: "pointer",
                              fontWeight: "bold",
                            }}
                          >
                            Cancelar
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        title="Intercambiar"
                        onClick={() => abrirIntercambio(pedido)}
                        style={{
                          backgroundColor: "#8a2be2", // morado para diferenciar
                          color: "white",
                          border: "none",
                          padding: "10px 12px",
                          borderRadius: 10,
                          cursor: "pointer",
                          fontWeight: "bold",
                          fontSize: 20,
                          lineHeight: 1,
                          boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
                        }}
                      >
                        ↔️
                      </button>
                    )}
                    {!(swapUI.abierto && swapUI.pedidoId === pedido.id) && (
                      <button
                        onClick={() => abrirModalEntrega(pedido)}
                        style={{
                          backgroundColor: "#28a745",
                          color: "white",
                          border: "none",
                          padding: "8px 12px",
                          borderRadius: 8,
                          cursor: "pointer",
                          fontWeight: "bold",
                          boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
                        }}
                      >
                        Entregar
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {/* Modal de entrega */}
      {modalEntrega.abierto && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            overflowY: "auto",
            zIndex: 9999,
            paddingTop: 20,
            paddingBottom: 20,
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: 10,
              padding: 20,
              width: "min(320px, 92vw)",
              maxHeight: "90vh",
              overflowY: "auto",
              WebkitOverflowScrolling: "touch",
            }}
          >
            <h3>
              Entregar {modalEntrega.pedido.abreviatura_piso}
              {modalEntrega.pedido.n_bolsas ? ` (${modalEntrega.pedido.n_bolsas} bolsas)` : ""}
            </h3>
            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 10 }}>
              <label>
                <input
                  type="radio"
                  value="entregado"
                  checked={estadoSeleccionado === "entregado"}
                  onChange={(e) => setEstadoSeleccionado(e.target.value)}
                />{" "}
                Entregado
              </label>
              <label>
                <input
                  type="radio"
                  value="en la puerta"
                  checked={estadoSeleccionado === "en la puerta"}
                  onChange={(e) => setEstadoSeleccionado(e.target.value)}
                />{" "}
                En la puerta
              </label>
            </div>

            {/* Captura de foto cuando es "en la puerta" */}
            {estadoSeleccionado === 'en la puerta' && (
              <div style={{ marginTop: 12 }}>
                <div style={{ marginBottom: 8, fontWeight: 700 }}>Foto de la entrega (obligatoria):</div>
                <div style={{ display: 'flex', gap: 10 }}>
                  {/* Botón cámara */}
                  <label
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative',
                      flex: 1,
                      minHeight: 100,
                      background: '#e8f5e9',
                      color: '#1b5e20',
                      border: '2px dashed #66bb6a',
                      borderRadius: 10,
                      cursor: 'pointer',
                      fontWeight: 800,
                      fontSize: 16,
                      overflow: 'hidden',
                      flexDirection: 'column',
                      gap: 4,
                    }}
                  >
                    📷<span style={{ fontSize: 12 }}>Hacer foto</span>
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      style={{
                        position: 'absolute',
                        width: '1px',
                        height: '1px',
                        padding: 0,
                        margin: '-1px',
                        overflow: 'hidden',
                        clip: 'rect(0,0,0,0)',
                        whiteSpace: 'nowrap',
                        borderWidth: 0,
                      }}
                      onChange={async (e) => {
                        const f = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                        if (!f) {
                          try { if (previewRef.current) { URL.revokeObjectURL(previewRef.current); previewRef.current = null; } } catch(_) {}
                          setPreviewUrl(null);
                          setFotoEntrega(null);
                          return;
                        }
                        setProcesandoFoto(true);
                        try { if (previewRef.current) { URL.revokeObjectURL(previewRef.current); previewRef.current = null; } } catch(_) {}
                        setPreviewUrl(null);
                        try {
                          const maxDim = f.size > 5 * 1024 * 1024 ? 1200 : 1600;
                          const quality = f.size > 5 * 1024 * 1024 ? 0.5 : 0.7;
                          const compressed = await compressImage(f, maxDim, quality);
                          let finalFile = compressed || f;
                          if (!(finalFile instanceof File)) {
                            const name = (f.name || 'foto').replace(/\.[^.]+$/, '') + '.jpg';
                            try { finalFile = new File([finalFile], name, { type: 'image/jpeg' }); }
                            catch (_) { finalFile.name = finalFile.name || (f.name || 'foto.jpg'); }
                          }
                          setFotoEntrega(finalFile);
                          try { const url = URL.createObjectURL(finalFile); previewRef.current = url; setPreviewUrl(url); }
                          catch (_) { setPreviewUrl(null); }
                        } catch (err) {
                          console.warn('Error procesando foto:', err);
                          setFotoEntrega(f);
                          try { const url = URL.createObjectURL(f); previewRef.current = url; setPreviewUrl(url); } catch(_) { setPreviewUrl(null); }
                        } finally {
                          setProcesandoFoto(false);
                        }
                      }}
                    />
                  </label>

                  {/* Botón galería */}
                  <label
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative',
                      flex: 1,
                      minHeight: 100,
                      background: '#e8f0ff',
                      color: '#1a237e',
                      border: '2px dashed #7986cb',
                      borderRadius: 10,
                      cursor: 'pointer',
                      fontWeight: 800,
                      fontSize: 16,
                      overflow: 'hidden',
                      flexDirection: 'column',
                      gap: 4,
                    }}
                  >
                    🖼️<span style={{ fontSize: 12 }}>Galería</span>
                    <input
                      type="file"
                      accept="image/*"
                      style={{
                        position: 'absolute',
                        width: '1px',
                        height: '1px',
                        padding: 0,
                        margin: '-1px',
                        overflow: 'hidden',
                        clip: 'rect(0,0,0,0)',
                        whiteSpace: 'nowrap',
                        borderWidth: 0,
                      }}
                      onChange={async (e) => {
                        const f = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                        if (!f) {
                          try { if (previewRef.current) { URL.revokeObjectURL(previewRef.current); previewRef.current = null; } } catch(_) {}
                          setPreviewUrl(null);
                          setFotoEntrega(null);
                          return;
                        }
                        setProcesandoFoto(true);
                        try { if (previewRef.current) { URL.revokeObjectURL(previewRef.current); previewRef.current = null; } } catch(_) {}
                        setPreviewUrl(null);
                        try {
                          const maxDim = f.size > 5 * 1024 * 1024 ? 1200 : 1600;
                          const quality = f.size > 5 * 1024 * 1024 ? 0.5 : 0.7;
                          const compressed = await compressImage(f, maxDim, quality);
                          let finalFile = compressed || f;
                          if (!(finalFile instanceof File)) {
                            const name = (f.name || 'foto').replace(/\.[^.]+$/, '') + '.jpg';
                            try { finalFile = new File([finalFile], name, { type: 'image/jpeg' }); }
                            catch (_) { finalFile.name = finalFile.name || (f.name || 'foto.jpg'); }
                          }
                          setFotoEntrega(finalFile);
                          try { const url = URL.createObjectURL(finalFile); previewRef.current = url; setPreviewUrl(url); }
                          catch (_) { setPreviewUrl(null); }
                        } catch (err) {
                          console.warn('Error procesando foto:', err);
                          setFotoEntrega(f);
                          try { const url = URL.createObjectURL(f); previewRef.current = url; setPreviewUrl(url); } catch(_) { setPreviewUrl(null); }
                        } finally {
                          setProcesandoFoto(false);
                        }
                      }}
                    />
                  </label>
                </div>
                {fotoEntrega && previewUrl && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ fontSize: 13, color: '#333', marginBottom: 6 }}>Previsualización:</div>
                    <img
                      src={previewUrl}
                      alt="Foto entrega"
                      style={{ width: '100%', maxHeight: 260, objectFit: 'contain', borderRadius: 8, border: '1px solid #ddd' }}
                    />
                    <div style={{ marginTop: 6, fontSize: 12, color: '#555' }}>
                      Tamaño: {formatBytes(fotoEntrega.size || 0)}
                    </div>
                  </div>
                )}
                {procesandoFoto && (
                  <div style={{ marginTop: 8, fontSize: 13, color: '#1b5e20' }}>
                    ⏳ Comprimiendo foto...
                  </div>
                )}
                {subiendo && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ height: 8, background: '#eee', borderRadius: 6, overflow: 'hidden' }}>
                      <div
                        style={{
                          width: `${uploadProgress}%`,
                          height: '100%',
                          background: '#28a745',
                          transition: 'width .2s ease',
                        }}
                      />
                    </div>
                    <div style={{ fontSize: 12, textAlign: 'right', marginTop: 4 }}>{uploadProgress}%</div>
                  </div>
                )}
              </div>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 20, gap: 10 }}>
              <button
                onClick={cerrarModalEntrega}
                style={{
                  padding: "10px 16px",
                  fontSize: "16px",
                  borderRadius: 8,
                  cursor: "pointer",
                }}
              >
                Cancelar
              </button>
              <button
                onClick={confirmarEntrega}
                style={{
                  backgroundColor: "#28a745",
                  color: "white",
                  padding: "10px 16px",
                  fontSize: "16px",
                  borderRadius: 8,
                  cursor: "pointer",
                  fontWeight: "bold",
                }}
                disabled={subiendo || procesandoFoto || (estadoSeleccionado === 'en la puerta' && !fotoEntrega)}
              >
                {subiendo ? 'Subiendo...' : procesandoFoto ? 'Preparando...' : 'Aceptar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
